// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file SocketContext.jsx
 * @description Contexto centralizado para WebSocket con estado de conexión
 * @version 4.0.0 - CORREGIDO: Estado socketReady y manejo de cola
 */

import { createContext, useContext, useEffect, useState } from 'react';
import socketService from '../services/socketService';
import { logEvent } from '../utils/runtimeLogger';
import { useAuth } from './AuthContext';

const SocketContext = createContext({
  socket: null,
  isConnected: false,
  socketReady: false, // CORRECCIÓN: Estado específico para envío
  pendingMessages: 0,
  connect: () => {},
  disconnect: () => {},
  emit: () => {},
  on: () => {},
  off: () => {},
  sendMessageResilient: () => {},
  waitForReady: () => Promise.resolve(false),
  getQueueStats: () => ({})
});

/**
 * Provider de Socket - Garantiza una sola conexión global con estado de cola
 */
export const SocketProvider = ({ children }) => {
  console.log('🔌 [SOCKET_PROVIDER] Renderizando SocketProvider');
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [socketReady, setSocketReady] = useState(false); // CORRECCIÓN: Estado específico
  const [pendingMessages, setPendingMessages] = useState(0);

  // CORRECCIÓN: Verificar estado del socket más frecuentemente
  useEffect(() => {
    const checkSocketState = () => {
      const connected = socketService.isConnected;
      const ready = socketService.socketReady;
      const stats = socketService.getQueueStats();

      setIsConnected(connected);
      setSocketReady(ready);
      setPendingMessages(stats.pendingMessages);

      // Log detallado del estado
      if (connected !== socketService.isConnected || ready !== socketService.socketReady) {
        console.log('🔄 [SOCKET_PROVIDER] Estado actualizado:', {
          isConnected: connected,
          socketReady: ready,
          pendingMessages: stats.pendingMessages,
          pendingEvents: stats.pendingEvents
        });
      }
    };

    // Verificar estado inicial
    checkSocketState();
    
    // Polling cada 500ms para estado de conexión
    const interval = setInterval(checkSocketState, 500);
    
    return () => clearInterval(interval);
  }, []);

  /**
   * Ciclo de vida del socket: depende solo del id de usuario (`id` o `usuario_id` según el backend).
   */
  const socketUserId = user?.id || user?.usuario_id;

  useEffect(() => {
    if (!socketUserId) {
      logEvent('WS_SKIP_NO_USER', 'socketContext');
      return;
    }

    logEvent('WS_CONTEXT_INIT', 'socketContext', { userId: socketUserId });

    socketService.connect(socketUserId);

    return () => {
      logEvent('WS_CONTEXT_CLEANUP', 'socketContext');
      socketService.disconnect();
    };
  }, [socketUserId]);

  const value = {
    socket: socketService.socket,
    isConnected,
    socketReady, // CORRECCIÓN: Exponer estado específico de envío
    pendingMessages,
    connect: (userId) => {
      const normalizedUserId = Number(userId || 0) || null;
      if (!normalizedUserId) return;
      console.log('🔌 [SOCKET_PROVIDER] Conectar manual para usuario:', userId);
      socketService.disconnect();
      socketService.connect(normalizedUserId);
    },
    disconnect: () => {
      console.log('🔌 [SOCKET_PROVIDER] Desconectar manual');
      socketService.disconnect();
      setIsConnected(false);
      setSocketReady(false);
      setPendingMessages(0);
    },
    emit: socketService.emit.bind(socketService),
    on: socketService.on.bind(socketService),
    off: socketService.off.bind(socketService),
    emitWithAck: socketService.emitWithAck.bind(socketService),
    // CORRECCIÓN: Métodos específicos para envío resiliente
    sendMessageResilient: socketService.sendMessageResilient.bind(socketService),
    waitForReady: socketService.waitForReady.bind(socketService),
    getQueueStats: socketService.getQueueStats.bind(socketService)
  };

  console.log('🔌 [SOCKET_PROVIDER] Renderizando children con estado:', {
    isConnected,
    socketReady,
    pendingMessages
  });

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

/**
 * Hook para usar el contexto de Socket
 */
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket debe usarse dentro de SocketProvider');
  }
  return context;
};

export default SocketContext;